// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useCallback, useMemo } from 'react';
import type { StepComponentProps } from '../../types/wizard';
import type { InvestmentFundEntry } from '../../types/providerAccess';

const EMPTY_FUND: InvestmentFundEntry = {};
const EMPTY_FUNDS: InvestmentFundEntry[] = [];

const RISK_RATING_OPTIONS = [
  { value: '1', text: '1' },
  { value: '2', text: '2' },
  { value: '3', text: '3' },
  { value: '4', text: '4' },
  { value: '5', text: '5' },
  { value: '6', text: '6' },
  { value: '7', text: '7' },
];

/** Strip non-numeric chars for percentage, keep single dot */
function toRawPercent(display: string): string {
  const stripped = display.replace(/[^0-9.]/g, '');
  const parts = stripped.split('.');
  if (parts.length <= 1) return stripped;
  return parts[0] + '.' + parts.slice(1).join('');
}

// Select chevron SVG
const chevronIcon = (
  <svg className="absolute right-[16px] top-1/2 -translate-y-1/2 pointer-events-none" width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M1 1.5L6 6.5L11 1.5" stroke="#525252" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// Trash icon SVG
const trashIcon = (id: string) => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g clipPath={`url(#clip_trash_inv_${id})`}>
      <mask id={`mask_trash_inv_${id}`} style={{ maskType: 'luminance' }} maskUnits="userSpaceOnUse" x="0" y="0" width="16" height="16">
        <path d="M15.5 0.5V15.5H0.5V0.5H15.5Z" fill="white" stroke="white"/>
      </mask>
      <g mask={`url(#mask_trash_inv_${id})`}>
        <path d="M14.0004 4.4873C13.9871 4.4873 13.9671 4.4873 13.9471 4.4873C10.4204 4.13397 6.90042 4.00064 3.41377 4.35397L2.05377 4.4873C1.77377 4.51397 1.5271 4.31397 1.50043 4.03397C1.47377 3.75397 1.67377 3.51397 1.9471 3.4873L3.3071 3.35397C6.85375 2.99397 10.4471 3.13397 14.0471 3.4873C14.3204 3.51397 14.5204 3.76064 14.4938 4.03397C14.4738 4.29397 14.2538 4.4873 14.0004 4.4873Z" fill="#2D0A6C"/>
        <path d="M5.66749 3.8135C5.64082 3.8135 5.61415 3.8135 5.58082 3.80683C5.31415 3.76016 5.12749 3.50016 5.17415 3.2335L5.32082 2.36016C5.42749 1.72016 5.57415 0.833496 7.12748 0.833496H8.87415C10.4341 0.833496 10.5808 1.7535 10.6808 2.36683L10.8275 3.2335C10.8741 3.50683 10.6875 3.76683 10.4208 3.80683C10.1475 3.8535 9.88748 3.66683 9.84748 3.40016L9.70081 2.5335C9.60748 1.9535 9.58748 1.84016 8.88081 1.84016H7.13415C6.42749 1.84016 6.41415 1.9335 6.31415 2.52683L6.16082 3.3935C6.12082 3.64016 5.90749 3.8135 5.66749 3.8135Z" fill="#2D0A6C"/>
        <path d="M10.1406 15.1667H5.86058C3.53392 15.1667 3.44058 13.8801 3.36725 12.8401L2.93392 6.12672C2.91392 5.85338 3.12725 5.61338 3.40058 5.59338C3.68058 5.58005 3.91392 5.78672 3.93392 6.06005L4.36725 12.7734C4.44058 13.7867 4.46725 14.1667 5.86058 14.1667H10.1406C11.5406 14.1667 11.5672 13.7867 11.6339 12.7734L12.0672 6.06005C12.0872 5.78672 12.3273 5.58005 12.6006 5.59338C12.8739 5.61338 13.0872 5.84672 13.0672 6.12672L12.6339 12.8401C12.5606 13.8801 12.4672 15.1667 10.1406 15.1667Z" fill="#2D0A6C"/>
        <path d="M9.10673 11.5H6.88673C6.61339 11.5 6.38672 11.2733 6.38672 11C6.38672 10.7267 6.61339 10.5 6.88673 10.5H9.10673C9.38007 10.5 9.60673 10.7267 9.60673 11C9.60673 11.2733 9.38007 11.5 9.10673 11.5Z" fill="#141414"/>
        <path d="M9.66732 8.8335H6.33398C6.06065 8.8335 5.83398 8.60683 5.83398 8.3335C5.83398 8.06016 6.06065 7.8335 6.33398 7.8335H9.66732C9.94065 7.8335 10.1673 8.06016 10.1673 8.3335C10.1673 8.60683 9.94065 8.8335 9.66732 8.8335Z" fill="#141414"/>
      </g>
    </g>
    <defs>
      <clipPath id={`clip_trash_inv_${id}`}>
        <rect width="16" height="16" fill="white"/>
      </clipPath>
    </defs>
  </svg>
);

// Add icon SVG
const addIcon = (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g clipPath="url(#clip_add_inv)">
      <mask id="mask_add_inv" style={{ maskType: 'luminance' }} maskUnits="userSpaceOnUse" x="0" y="0" width="16" height="16">
        <path d="M15.5 0.5V15.5H0.5V0.5H15.5Z" fill="white" stroke="white"/>
      </mask>
      <g mask="url(#mask_add_inv)">
        <path d="M10.6663 8.5H5.33301C5.05967 8.5 4.83301 8.27333 4.83301 8C4.83301 7.72667 5.05967 7.5 5.33301 7.5H10.6663C10.9397 7.5 11.1663 7.72667 11.1663 8C11.1663 8.27333 10.9397 8.5 10.6663 8.5Z" fill="#2D0A6C"/>
        <path d="M8 11.1668C7.72667 11.1668 7.5 10.9402 7.5 10.6668V5.3335C7.5 5.06016 7.72667 4.8335 8 4.8335C8.27333 4.8335 8.5 5.06016 8.5 5.3335V10.6668C8.5 10.9402 8.27333 11.1668 8 11.1668Z" fill="#2D0A6C"/>
        <path d="M9.99967 15.1668H5.99967C2.37967 15.1668 0.833008 13.6202 0.833008 10.0002V6.00016C0.833008 2.38016 2.37967 0.833496 5.99967 0.833496H9.99967C13.6197 0.833496 15.1663 2.38016 15.1663 6.00016V10.0002C15.1663 13.6202 13.6197 15.1668 9.99967 15.1668ZM5.99967 1.8335C2.92634 1.8335 1.83301 2.92683 1.83301 6.00016V10.0002C1.83301 13.0735 2.92634 14.1668 5.99967 14.1668H9.99967C13.073 14.1668 14.1663 13.0735 14.1663 10.0002V6.00016C14.1663 2.92683 13.073 1.8335 9.99967 1.8335H5.99967Z" fill="#2D0A6C"/>
      </g>
    </g>
    <defs>
      <clipPath id="clip_add_inv">
        <rect width="16" height="16" fill="white"/>
      </clipPath>
    </defs>
  </svg>
);

export function StepInvestments({
  data,
  onFieldChange,
}: StepComponentProps) {
  const funds = useMemo(
    () => (data.investmentFunds as InvestmentFundEntry[]) || EMPTY_FUNDS,
    [data.investmentFunds],
  );

  const updateFund = useCallback(
    (index: number, field: keyof InvestmentFundEntry, value: unknown) => {
      const updated = [...funds];
      updated[index] = { ...updated[index], [field]: value };
      onFieldChange('investmentFunds', updated);
    },
    [funds, onFieldChange],
  );

  const addFund = useCallback(() => {
    onFieldChange('investmentFunds', [...funds, { ...EMPTY_FUND }]);
  }, [funds, onFieldChange]);

  const removeFund = useCallback(
    (index: number) => {
      const updated = funds.filter((_, i) => i !== index);
      onFieldChange('investmentFunds', updated);
    },
    [funds, onFieldChange],
  );

  const handlePercentChange = (index: number, field: keyof InvestmentFundEntry, inputValue: string) => {
    const raw = toRawPercent(inputValue);
    if (raw && !/^\d*\.?\d*$/.test(raw)) return;
    updateFund(index, field, raw);
  };

  const inputClass =
    'w-full h-[48px] px-md rounded-lg border border-grey-04 bg-white text-[#333] font-sans text-body-02 font-normal leading-normal outline-none focus:border-purple-primary';

  const selectClass =
    'w-full h-[48px] px-md pr-xxl rounded-lg border border-grey-04 bg-white text-[#333] font-sans text-body-02 font-normal leading-normal outline-none focus:border-purple-primary appearance-none cursor-pointer';

  const percentWrapper =
    'flex items-center w-full h-[48px] px-md rounded-lg border border-grey-04 bg-white focus-within:border-purple-primary';

  return (
    <div className="flex flex-col gap-lg">
      <div className="text-black font-sans text-h5 font-normal leading-normal not-italic">
        Investments
      </div>
      {funds.map((fund, index) => (
        <div key={index} className="flex flex-col gap-md bg-lilac-06 rounded-button px-lg py-[20px]">
          {/* Row 1: Fund name | Weighting % Allocation */}
          <div className="flex flex-row gap-lg">
            <div className="flex flex-col gap-sm flex-1">
              <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
                Investment fund name <span className="text-[#E7000B]">*</span>
              </div>
              <input
                type="text"
                placeholder=""
                value={fund.fundName || ''}
                onChange={(e) => updateFund(index, 'fundName', e.target.value)}
                className={inputClass}
              />
            </div>

            <div className="flex flex-col gap-sm flex-1">
              <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
                Weighting % Allocation of Investment Funds <span className="text-[#E7000B]">*</span>
              </div>
              <div className={percentWrapper}>
                <input
                  type="text"
                  inputMode="decimal"
                  value={fund.allocationPercent || ''}
                  onChange={(e) => handlePercentChange(index, 'allocationPercent', e.target.value)}
                  className="flex-1 h-full bg-transparent text-[#333] font-sans text-body-02 font-normal leading-normal outline-none"
                />
                <span className="text-grey-04 font-sans text-body-02 font-normal leading-normal select-none ml-sm">%</span>
              </div>
            </div>
          </div>

          {/* Row 2: Future contribution % | Risk Rating */}
          <div className="flex flex-row gap-lg items-end">
            <div className="flex flex-col gap-sm flex-1">
              <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
                Weighting % Allocation of Investment Funds for future contributions
              </div>
              <div className={percentWrapper}>
                <input
                  type="text"
                  inputMode="decimal"
                  value={fund.futureContributionPercent || ''}
                  onChange={(e) => handlePercentChange(index, 'futureContributionPercent', e.target.value)}
                  className="flex-1 h-full bg-transparent text-[#333] font-sans text-body-02 font-normal leading-normal outline-none"
                />
                <span className="text-grey-04 font-sans text-body-02 font-normal leading-normal select-none ml-sm">%</span>
              </div>
            </div>

            <div className="flex flex-col gap-sm flex-1">
              <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
                Risk Rating of investment fund <span className="text-[#E7000B]">*</span>
              </div>
              <div className="relative">
                <select
                  value={fund.riskRating || ''}
                  onChange={(e) => updateFund(index, 'riskRating', e.target.value)}
                  className={`${selectClass} ${fund.riskRating ? 'text-[#333]' : 'text-[#B6B6B6]'}`}
                >
                  <option value="" disabled className="text-[#B6B6B6]">Choose an option</option>
                  {RISK_RATING_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value} className="text-[#333]">{o.text}</option>
                  ))}
                </select>
                {chevronIcon}
              </div>
            </div>
          </div>

          {/* Row 3: Annual Management Charge | Total Expense Ratio */}
          <div className="flex flex-row gap-lg">
            <div className="flex flex-col gap-sm flex-1">
              <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
                Annual Management Charge <span className="text-[#E7000B]">*</span>
              </div>
              <div className={percentWrapper}>
                <input
                  type="text"
                  inputMode="decimal"
                  value={fund.annualManagementCharge || ''}
                  onChange={(e) => handlePercentChange(index, 'annualManagementCharge', e.target.value)}
                  className="flex-1 h-full bg-transparent text-[#333] font-sans text-body-02 font-normal leading-normal outline-none"
                />
                <span className="text-grey-04 font-sans text-body-02 font-normal leading-normal select-none ml-sm">%</span>
              </div>
            </div>

            <div className="flex flex-col gap-sm flex-1">
              <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
                Total Expense Ratio (TER) <span className="text-[#E7000B]">*</span>
              </div>
              <div className={percentWrapper}>
                <input
                  type="text"
                  inputMode="decimal"
                  value={fund.totalExpenseRatio || ''}
                  onChange={(e) => handlePercentChange(index, 'totalExpenseRatio', e.target.value)}
                  className="flex-1 h-full bg-transparent text-[#333] font-sans text-body-02 font-normal leading-normal outline-none"
                />
                <span className="text-grey-04 font-sans text-body-02 font-normal leading-normal select-none ml-sm">%</span>
              </div>
            </div>
          </div>

          {/* Row 4: Performance Fees | Bid/Offer Spread */}
          <div className="flex flex-row gap-lg">
            <div className="flex flex-col gap-sm flex-1">
              <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
                Performance Fees <span className="text-[#E7000B]">*</span>
              </div>
              <div className={percentWrapper}>
                <input
                  type="text"
                  inputMode="decimal"
                  value={fund.performanceFees || ''}
                  onChange={(e) => handlePercentChange(index, 'performanceFees', e.target.value)}
                  className="flex-1 h-full bg-transparent text-[#333] font-sans text-body-02 font-normal leading-normal outline-none"
                />
                <span className="text-grey-04 font-sans text-body-02 font-normal leading-normal select-none ml-sm">%</span>
              </div>
            </div>

            <div className="flex flex-col gap-sm flex-1">
              <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
                Bid/Offer Spread <span className="text-[#E7000B]">*</span>
              </div>
              <div className={percentWrapper}>
                <input
                  type="text"
                  inputMode="decimal"
                  value={fund.bidOfferSpread || ''}
                  onChange={(e) => handlePercentChange(index, 'bidOfferSpread', e.target.value)}
                  className="flex-1 h-full bg-transparent text-[#333] font-sans text-body-02 font-normal leading-normal outline-none"
                />
                <span className="text-grey-04 font-sans text-body-02 font-normal leading-normal select-none ml-sm">%</span>
              </div>
            </div>
          </div>

          {/* Row 5: Market Value Adjustment (MVA) / With Profits */}
          <div className="flex flex-row gap-lg">
            <div className="flex flex-col gap-sm flex-1">
              <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
                Market Value Adjustment (MVA) / With Profits <span className="text-[#E7000B]">*</span>
              </div>
              <input
                type="text"
                value={fund.marketValueAdjustment || ''}
                onChange={(e) => updateFund(index, 'marketValueAdjustment', e.target.value)}
                className={inputClass}
              />
            </div>
            <div className="flex-1" />
          </div>

          {/* Remove fund button */}
          <button
            type="button"
            onClick={() => removeFund(index)}
            className="flex items-center gap-sm cursor-pointer bg-transparent border-none p-0 w-fit"
          >
            {trashIcon(String(index))}
            <div className="text-purple-primary font-sans text-body-02 font-medium leading-normal capitalize">
              Remove this fund
            </div>
          </button>
        </div>
      ))}

      {/* Add fund button */}
      <button
        type="button"
        onClick={addFund}
        className="flex items-center gap-sm cursor-pointer bg-transparent border-none p-0 w-fit"
      >
        {addIcon}
        <div className="text-purple-primary font-sans text-body-02 font-medium leading-normal capitalize">
          Add Another Fund
        </div>
      </button>
    </div>
  );
}
